import React, { useCallback, useState } from "react";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Modal, { Header, Footer, Body } from "~/common/ModalReact";
import Button from "~/common/Buttons";
import { AddToCartModal } from ".";
import { Accessory } from "../Store/models/ProductListing";
import AccessoryPopover from "~/ProductDetails/Pricing/AddToCart/AccessoryPopover";
interface Props extends AddToCartModal<"accessories"> {
  accessories: Readonly<Accessory[]>;
  itemNumber: string;
  previouslyPurchased?: boolean;
}
export default ({
  isOpen,
  accessories,
  itemNumber,
  closeModal,
  submitModal,
  previouslyPurchased = false
}: Props) => {
  interface Values {
    [index: string]: string;
  }
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const setModalRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setPortalContainer(node); // Store in state to trigger re-render for Modal content
    }
  }, []);
  const valid = () => {
    const newErrors: string[] = [];
    accessories.forEach(accessory => {
      if (!accessory.isRequired || values[accessory.name]) return;
      newErrors.push(accessory.name);
    });
    setErrors(newErrors);
    return !newErrors.length;
  };
  const handleAddToCart = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (valid()) {
      submitModal({
        accessories: Object.values(values)
      });
      setValues({});
    }
  };
  const validateSingle = (accessoryName: string) => {
    setErrors([...errors].filter(error => error !== accessoryName));
  };
  return <Modal isOpen={isOpen} onRequestClose={closeModal} className="w-11/12 md:max-w-md">
      <Header closeModal={closeModal}>
        <h4 className="flex m-0 text-base md:text-xl">
          {`Product Accessories for ${itemNumber.toUpperCase()}`}
        </h4>
      </Header>

      <Body>
        <div className="block">
          <div id="accessoriesListOverlayContainer" className="accessoriesList form" ref={setModalRef}>
            <p className="small">
              This item has further options/accessories available.
            </p>
            {accessories.map(accessory => <div key={accessory.name} className="form__group">
                <div className={`form__control-container${errors.includes(accessory.name) ? " error" : ""}${accessory.extendedInfo ? " form__control-container--popover" : ""}`}>
                  {accessory.extendedInfo && <span className="form__popover" data-testid="extended-info-popover">
                      <AccessoryPopover accessoryName={accessory.name} extendedInfo={accessory.extendedInfo} placement="top" portalContainer={portalContainer ?? undefined} />
                    </span>}
                  <select value={values[accessory.name] ?? ""} onChange={e => {
                setValues({
                  ...values,
                  [accessory.name]: e.target.value
                });
                validateSingle(accessory.name);
              }} name="accessories" className="form__control" title={accessory.name}>
                    <option value="" disabled>
                      --No Selection--
                    </option>
                    {accessory.options.map(option => <option key={option.id} value={option.id} disabled={!option.available}>
                        {option.available ? `${option.description} - ${formatAsCurrency.formatIfUndefined(Number(option.price))}` : `${option.description} [OUT OF STOCK]`}
                      </option>)}
                  </select>
                  <label className="form__label">
                    {accessory.name}
                    {accessory.isRequired && <>
                        {" - "}
                        <span className="label__required">Required</span>
                      </>}
                  </label>
                  <i className="icon-down-micro form__control-icon form__control-icon--lg" />
                </div>
              </div>)}
            {errors[0] && <p className="messages">
                <span className="form__error">
                  {`Please select a ${errors[0]} for this product.`}
                </span>
              </p>}
          </div>
        </div>
      </Body>
      <Footer>
        <Button color="light" className="mr-3" type="button" onClick={closeModal}>
          Cancel
        </Button>
        <Button color="red" type="submit" onClick={handleAddToCart} data-testid="accessory-modal-addtocart-button" gtm-id={previouslyPurchased ? "BuyItAgainATC" : "AddToCartATC"}>
          Add To Cart
        </Button>
      </Footer>
    </Modal>;
};